/**
 * Versiegeschiedenis van de applicatie (US-035).
 *
 * Twee lijsten met een scherpe grens ertussen. De regels staan in `docs/VERSIONING.md`.
 *
 * - `RELEASES` zijn de officiële productieversies, `MAJOR.MINOR.PATCH`. Elke release heeft precies
 *   één annotated Git-tag (`v` + het nummer) en één GitHub Release, en is daarmee onveranderlijk te
 *   herleiden tot één commit. De lijst begint leeg: de eerste release (1.0.0) volgt na acceptatie
 *   van staging. Een regel komt hier bij de voorbereiding van een release, niet bij elke commit.
 * - `VOORGESCHIEDENIS` is het logboek per commit (V1.01 t/m V1.99) uit de tijd vóór de eerste
 *   release. Het is afgesloten: er komen geen regels meer bij, en een test bewaakt dat.
 *
 * Waarom twee lijsten en niet één: `VERSIONING.md` §11 verbiedt meerdere onafhankelijke bronnen met
 * verschillende nummers. De oude teller liep op per commit en zou daarom vanaf nu tegenspreken met
 * de GitHub Releases. Doorgaan kon dus niet, en weggooien wilden we niet — er zit de enige
 * beschrijving in van wat er per stap is veranderd. Daarom stopt hij en blijft hij bewaard.
 *
 * Waarom in de code en niet in de database: een tabel zou per omgeving anders kunnen zijn, en dan
 * zegt "1.4.0" in staging iets anders dan in productie. Het versienummer hoort bij de code die
 * draait. Dat nummer staat in het `package.json` in de hoofdmap (de enige bron, §9); de build
 * schrijft het weg in `build-info.json` en een test bewaakt dat de bovenste release hiermee
 * overeenkomt.
 *
 * "Draait mijn wijziging al?" beantwoordt niet dit bestand maar de commit die in het menu staat
 * naast het versienummer: elke omgeving meldt de commit van de code die er werkelijk draait.
 */
/** Het formaat van een releaseversie: `1.4.0`, zonder voorloopnullen (semver). */
export const RELEASE_VERSIE = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
/** Het formaat van een releasetag: `v1.4.0`. */
export const RELEASE_TAG = /^v(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
/** De Git-tag bij een releaseversie: `1.4.0` → `v1.4.0`. */
export function releaseTag(versie) {
    return `v${versie}`;
}
/**
 * Officiële releases, nieuwste eerst. Dat is de leesrichting van zo'n lijst én het maakt de
 * huidige release de eerste regel.
 */
export const RELEASES = [
    {
        versie: '1.0.1',
        datum: '2026-10-07',
        titel: 'Technische correctie: releasetag van 1.0.0 was niet annotated',
        punten: [
            'De tag v1.0.0 was een lichte tag en werd door de controle in de CI geweigerd. Daarom is 1.0.1 de eerste officiële release met een annotated tag. Een gepubliceerde tag wordt nooit verplaatst.',
            'Daarnaast zijn de logs en de beveiliging aangescherpt: gevoelige queryparameters van de Microsoft-aanmelding worden uit het logboek gehaald en elke respons krijgt een Permissions-Policy-header. Voor de gebruiker verandert er verder niets.',
        ],
    },
    {
        versie: '1.0.0',
        datum: '2026-10-06',
        titel: 'Eerste release: dagelijkse prijscontrole van de eigen webshop en de concurrenten',
        punten: [
            'Elke nacht om 00:15 worden de eigen verkoopprijs uit de XML-feed en de prijzen van de concurrenten per product gecontroleerd. Een prijs telt alleen mee als de lengte en, waar van toepassing, de kleur of folie-uitvoering aantoonbaar kloppen.',
            'Het prijsoverzicht laat gewijzigde prijzen opvallen, toont de laagste prijs en het verschil met onze eigen prijs in euro en procent. Per product is er een prijshistorie en een prijsgrafiek, en alle gegevens zijn te exporteren naar Excel.',
            'Bij een prijswijziging komt er één e-mail met alles wat is veranderd, en elke dag één overzicht van mislukte bronnen. De ontvangers zijn per bedrijf in te stellen. Een mislukte bron krijgt automatisch nog één nieuwe kans.',
            'Producten komen uit een Excelbestand, met vooraf een overzicht van wat wordt toegevoegd of overgeslagen. Prijsbronnen stel je in met een visuele configurator en test je voordat ze meedoen. AI helpt bij het analyseren van een pagina en bij het zoeken naar concurrentpagina’s.',
            'Inloggen gaat met Microsoft, alleen voor adressen op de toegangslijst. Rollen gelden per bedrijf, en gegevens van het ene bedrijf zijn nooit zichtbaar bij het andere.',
            'Wijzigingen worden eerst op een aparte testomgeving geprobeerd. In het menu staan de versie en de commit van de code die draait.',
        ],
    },
];
/**
 * Het logboek per commit, nieuwste eerst. Afgesloten bij V1.99: nieuwe wijzigingen krijgen hier geen
 * regel meer maar komen in de releasenotes bij de volgende release.
 */
export const VOORGESCHIEDENIS = [
    {
        versie: 'V1.99',
        datum: '2026-10-06',
        titel: 'Documentatie: één gebruiker voor staging en productie bewust aanvaard',
        punten: [
            'In het runbook staat nu vastgelegd dat staging en productie dezelfde servergebruiker delen, met de reden en wanneer dit opnieuw bekeken moet worden. Voor de gebruiker verandert er niets.',
        ],
        commit: '9b936f6',
    },
    {
        versie: 'V1.98',
        datum: '2026-10-06',
        titel: 'Deploy: het script draait nooit als root, en de werkelijke opzet staat in de repository',
        punten: [
            'Het deploy-script weigert nu te draaien als root en herstart de service alleen via een exact toegestaan sudo-commando. Voor de gebruiker verandert er niets.',
            'De wrapper en het sudoers-voorbeeld van de server staan in de repository, en het runbook beschrijft de opzet met een deploy key per omgeving in plaats van een token.',
        ],
        commit: 'a48d992',
    },
    {
        versie: 'V1.97',
        datum: '2026-10-06',
        titel: 'De applicatie meldt welke versie en commit er draait',
        punten: [
            'Onderin het menu staat nu ook de commit van de code die draait, zodat je kunt zien of een wijziging er al op staat.',
            'Bij een uitrol controleert het deploy-script of de applicatie daadwerkelijk de nieuwe code draait, en niet alleen of hij antwoordt.',
            'Het deploy-script kan een release uitrollen of terugzetten op een versienummer (tag).',
        ],
        commit: 'ac34fae',
    },
    {
        versie: 'V1.96',
        datum: '2026-10-06',
        titel: 'Documentatie: bindende regels voor security, deployment en versiebeheer toegevoegd',
        punten: [
            'De regels van de opdrachtgever voor security, deployment en versiebeheer staan nu in de documentatie en worden bij nieuw werk meegenomen. Voor de gebruiker verandert er niets.',
        ],
        commit: '8c37217',
    },
    {
        versie: 'V1.95',
        datum: '2026-10-02',
        titel: 'Onderhoud: de geautomatiseerde controle faalt als tests worden overgeslagen',
        punten: [
            'De controle die bij elke wijziging draait, geeft nu een fout als er tests zijn overgeslagen, bijvoorbeeld de browsertests. Voor de gebruiker verandert er niets.',
        ],
        commit: '2af18a7',
    },
    {
        versie: 'V1.94',
        datum: '2026-10-01',
        titel: 'Onderhoud: afhankelijkheden bijgewerkt (patchversies)',
        punten: [
            'Bibliotheken zijn bijgewerkt naar de nieuwste patchversies, onder meer voor de webserver en de opmaak van de code. Voor de gebruiker verandert er niets.',
        ],
        commit: 'e1a819e',
    },
    {
        versie: 'V1.93',
        datum: '2026-09-29',
        titel: 'Beveiliging: onafhankelijke sleutels die te vervangen zijn zonder dataverlies',
        punten: [
            'Inlogcookies en de opgeslagen feed-link hebben elk een eigen, losse sleutel in plaats van er één te delen. Een gelekte sleutel raakt het andere niet.',
            'Sleutels zijn te vervangen zonder dat de opgeslagen feed-link onleesbaar wordt: bij het opstarten wordt hij automatisch opnieuw versleuteld.',
            'Bij het opstarten controleert de applicatie of alle opgeslagen feed-links leesbaar zijn en meldt dat in het logboek en in de gezondheidscontrole.',
        ],
        commit: 'bcf0a23',
    },
    {
        versie: 'V1.92',
        datum: '2026-09-29',
        titel: 'Beveiliging: extra controle tegen verzoeken vanaf een andere website',
        punten: [
            'Handelingen die iets wijzigen worden nu alleen uitgevoerd als ze afkomstig zijn van de Prijsmonitor zelf. Een verzoek dat een andere website namens een ingelogde gebruiker probeert te sturen, wordt geweigerd.',
            'Dit komt bovenop de bestaande cookie-instelling die hetzelfde al tegenhield. Voor de gebruiker verandert er niets.',
        ],
        commit: '3c6e182',
    },
    {
        versie: 'V1.91',
        datum: '2026-09-29',
        titel: 'Beveiliging: aparte sleutels voor cookies en de opgeslagen feed-link',
        punten: [
            'Het ondertekenen van inlogcookies en het versleutelen van de opgeslagen feed-link gebruiken nu elk een eigen sleutel, afgeleid uit dezelfde hoofdsleutel. Lekt één van die twee, dan is de ander niet ook open.',
            'Een al opgeslagen feed-link blijft gewoon werken; die hoeft niet opnieuw te worden ingevoerd.',
            'Een inlogpoging die precies tijdens het uitrollen loopt, moet eenmalig opnieuw worden gestart.',
        ],
        commit: 'aae486c',
    },
    {
        versie: 'V1.90',
        datum: '2026-09-29',
        titel: 'Beveiliging: de crawlerbrowser draait weer met beschermlaag',
        punten: [
            'De automatische browser die de sites van concurrenten bezoekt, draait nu met zijn beschermlaag (sandbox) aan. Voorheen stond die altijd uit.',
            'De browser krijgt bovendien geen geheimen (sleutels, databasegegevens) meer mee in zijn omgeving.',
            'Lukt de beschermlaag op een server niet, dan meldt de crawl dat duidelijk. Er is een bewuste noodschakelaar voor, met een waarschuwing in het logboek.',
        ],
        commit: '3749359',
    },
    {
        versie: 'V1.89',
        datum: '2026-09-20',
        titel: 'Onderhoud: achterliggende software-onderdelen bijgewerkt',
        punten: [
            'Een aantal achterliggende software-onderdelen (waaronder de foutregistratie, Fastify, ESLint, Prettier en de routering in het scherm) is bijgewerkt naar de nieuwste kleine versie, als onderdeel van regulier onderhoud.',
            'Er is voor de gebruiker verder niets veranderd: geen nieuw scherm, geen ander gedrag.',
        ],
        commit: '7ad5b52',
    },
    {
        versie: 'V1.88',
        datum: '2026-09-18',
        titel: 'Alle mislukte bronnen in één keer opnieuw controleren',
        punten: [
            'Bij het foutoverzicht in Taken & logboek staat nu een knop "Controleer de mislukte", die alleen de bronnen uit die lijst opnieuw controleert.',
            'Voorheen kon dat alleen per bron apart, of door de hele dagelijkse controle (inclusief de bronnen die al goed stonden) opnieuw te laten lopen via "Nu controleren".',
        ],
        commit: 'd7c1e6e',
    },
    {
        versie: 'V1.87',
        datum: '2026-09-18',
        titel: 'Vanuit het foutoverzicht rechtstreeks naar de bronconfigurator',
        punten: [
            'Bij een mislukte bron in Taken & logboek staat nu een knop "Bron configureren" die direct naar de bronconfigurator gaat, met het product en die bron al gekozen.',
            'Voorheen moest bij elke mislukte bron eerst het product en daarna, tussen alle webshops, de juiste bron weer worden opgezocht.',
        ],
        commit: '03992c7',
    },
    {
        versie: 'V1.86',
        datum: '2026-09-13',
        titel: 'Onderhoud: achterliggende software-onderdelen bijgewerkt',
        punten: [
            'Een aantal achterliggende software-onderdelen (waaronder de foutregistratie, React, Fastify, ESLint en de inlogkoppeling) is bijgewerkt naar de nieuwste kleine versie, als onderdeel van regulier onderhoud.',
            'Er is voor de gebruiker verder niets veranderd: geen nieuw scherm, geen ander gedrag.',
        ],
        commit: '493f0e6',
    },
    {
        versie: 'V1.85',
        datum: '2026-09-08',
        titel: 'Nettocorrectie op de brutoprijs uit de feed, per product',
        punten: [
            'Voor twee bedrijven staat in de prijsfeed een brutoprijs die tot nu toe ongewijzigd als nettoprijs in de webshop werd gebruikt.',
            'Bij een product is nu een correctiepercentage in te vullen. Is dat ingevuld, dan wordt de nettoprijs berekend als de brutoprijs uit de feed min dat percentage. Staat het veld leeg, dan verandert er niets aan de bestaande prijs.',
        ],
        commit: '0f9b664',
    },
    {
        versie: 'V1.84',
        datum: '2026-09-08',
        titel: 'Een mislukte crawl krijgt automatisch nog één kans',
        punten: [
            'Een tijdelijke storing bij een webshop (bijvoorbeeld een blokkade of een timeout) belandde meteen in het dagelijkse foutoverzicht, ook al zou een nieuwe poging even later prima zijn gelukt.',
            "Zo'n bron wordt nu, ná de nachtelijke ronde en vóór het foutoverzicht, automatisch nog één keer gecontroleerd. Lukt die herpoging, dan telt de meting gewoon mee en verdwijnt de bron uit het foutoverzicht. Blijft de storing bestaan, dan staat de bron er nog steeds in.",
            'Fouten die niet met tijd te maken hebben, zoals een gewijzigde pagina of een probleem met de eigen prijsfeed, worden niet automatisch herhaald.',
        ],
        commit: 'bf7d81a',
    },
    {
        versie: 'V1.83',
        datum: '2026-09-07',
        titel: 'Verkeersdrempel was te omzeilen door een adres te verzinnen',
        punten: [
            'De applicatie beperkt het aantal verzoeken per bezoeker om overbelasting te voorkomen, maar geloofde daarbij het adres dat de bezoeker zelf meestuurde. Wie bij elk verzoek een ander adres opgaf, had daarmee geen enkele grens meer.',
            'Alleen de webserver op dezelfde machine mag nu doorgeven wie de bezoeker is. Dat adres wordt ook bij een sessie vastgelegd, dus het is nu ook in het logboek betrouwbaar.',
            'Geen zichtbare wijziging in de applicatie.',
        ],
        commit: 'f04fa79',
    },
    {
        versie: 'V1.82',
        datum: '2026-09-07',
        titel: 'Geen tokens meer in het logboek en de auditpoort opgeruimd',
        punten: [
            'Een token dat in een webadres meekomt, staat niet langer leesbaar in het logboek. De methode en het pad blijven wél staan, want die zijn juist nodig om een storing te kunnen uitzoeken.',
            "De lijst met beoordeelde beveiligingsmeldingen die de bouw niet mogen tegenhouden is leeg: de enige melding erop is verholpen. Die lijst gaf bij elke controle een waarschuwing dat hij verouderd was, en zo'n regel die er altijd staat leert iedereen om over de uitvoer heen te kijken.",
            'Geen zichtbare wijziging in de applicatie.',
        ],
        commit: 'd94c965',
    },
    {
        versie: 'V1.81',
        datum: '2026-09-07',
        titel: 'Twee beveiligingsgaten gedicht: interne adressen en te grote importbestanden',
        punten: [
            'De applicatie haalt geen adressen meer op die binnen ons eigen netwerk liggen. Een prijsbron, een feed-URL of een door de AI voorgestelde winkel die naar de server zelf of naar een intern adres wijst, wordt nu geweigerd met een melding die zegt waarom — bij het opslaan én bij elke nachtelijke controle.',
            'Een Excelbestand dat klein lijkt maar naar heel veel gegevens uitpakt, wordt geweigerd voordat het wordt ingelezen. Zulke bestanden legden de hele applicatie bijna een minuut stil, voor alle bedrijven tegelijk; ze worden nu binnen een seconde afgewezen.',
            'Voor een gewone import en een gewone prijsbron verandert er niets.',
        ],
        commit: 'f5a3416',
    },
    {
        versie: 'V1.80',
        datum: '2026-09-02',
        titel: "Werkafspraken verduidelijkt: PR's naar staging, main volgt via aparte promotie",
        punten: [
            'Geen wijziging voor de gebruiker. CLAUDE.md beschreef dat een commit na een groene `npm run verify` naar zowel main als staging gaat, terwijl in de praktijk staging al voorliep op main.',
            'Vastgelegd hoe het echt werkt: een taak krijgt een Pull Request met staging als base, wordt daar getest, en gaat pas via een aparte promotie-Pull Request naar main.',
        ],
        commit: '406a1e2',
    },
    {
        versie: 'V1.79',
        datum: '2026-09-02',
        titel: 'Beveiligingsfix in de Prisma-toolketen en kleine afhankelijkheden bijgewerkt',
        punten: [
            'Een hoog risico beveiligingsmelding (stack-overloop bij het samenvoegen van configuratie, GHSA-ggr8-5vv4-36mx) in een onderliggend hulppakket van de Prisma-commandoregeltool is verholpen zonder dat Prisma zelf hoeft te worden opgewaardeerd naar een nog niet stabiele hoofdversie.',
            'Verder alleen kleine, risicoloze bijwerkingen van bestaande afhankelijkheden (onder meer Fastify, Sentry, ESLint, Playwright en tsx) naar hun nieuwste toegestane versie; geen zichtbare wijziging voor de gebruiker.',
        ],
        commit: '7362309',
    },
    {
        versie: 'V1.78',
        datum: '2026-08-31',
        titel: 'Bedrijven blokkeren elkaar niet meer bij de nachtelijke controle',
        punten: [
            'Alle bedrijven werden ná elkaar gecontroleerd binnen dezelfde ronde. Duurde de controle van het eerste bedrijf langer dan verwacht, dan kwamen de bedrijven erna simpelweg niet aan de beurt — een handmatige "Nu controleren" bij één bedrijf leek daardoor ook de andere te starten, want ze wachtten in dezelfde rij.',
            'Ieder bedrijf krijgt nu zijn eigen starttijd: het eerste bedrijf om 00:15, elk volgende bedrijf een half uur later (op volgorde van aanmaken, in te stellen via DAILY_CHECK_SPREIDING_MINUTEN). Bedrijven staan zo niet meer op elkaar te wachten.',
        ],
        commit: '6a80570',
    },
    {
        versie: 'V1.77',
        datum: '2026-08-31',
        titel: 'URL van de servicecatalogus gecorrigeerd in de voorbeeldconfiguratie',
        punten: [
            'De voorbeeldwaarde voor SERVICE_CATALOGUE_URL stond per ongeluk op de eigen productie-URL van Prijsmonitor in plaats van op de URL van de servicecatalogus zelf. Gecorrigeerd naar de bevestigde URL.',
        ],
        commit: '7cc9e40',
    },
    {
        versie: 'V1.76',
        datum: '2026-08-31',
        titel: 'Melding aan de servicecatalogus bij het inloggen',
        punten: [
            'Na iedere geslaagde login meldt de applicatie nu optioneel het tijdstip en de naam van de gebruiker aan de servicecatalogus, zodat die kan tonen of Prijsmonitor nog gebruikt wordt.',
            'Er gaat alleen een naam en een tijdstip mee — nooit een e-mailadres, gebruikers-ID of IP-adres.',
            'Zonder ingesteld token blijft de melding uit. Een storing in deze koppeling kan de login nooit blokkeren of vertragen: er wordt niet op gewacht en er wordt niet opnieuw geprobeerd.',
        ],
        commit: '0ccd76f',
    },
    {
        versie: 'V1.75',
        datum: '2026-08-31',
        titel: 'Een vastgelopen controle kon niet worden gestopt',
        punten: [
            'Startte een deploy of een herstart halverwege de nachtelijke controle, dan bleef die ronde voor altijd op "bezig" staan: het proces dat hem uitvoerde bestond niet meer, maar er was niemand die dat vastlegde.',
            '"Nu controleren" weigerde daarna voorgoed met "Er loopt al een controle", zonder dat daar op het scherm iets aan te doen was.',
            'Bij Taken & logboek staat nu een knop "Stoppen" zodra een controle loopt. Die markeert de vastgelopen ronde als mislukt en sluit de nog openstaande bronnen af, zodat er meteen weer een nieuwe controle gestart kan worden.',
        ],
        commit: 'aeab259',
    },
    {
        versie: 'V1.74',
        datum: '2026-08-18',
        titel: 'Een aangewezen element kon een ander element zijn',
        punten: [
            'De selector die de configurator vastlegde was niet altijd eenduidig. Bij een pagina met gelijkvormige blokken — uitklappanelen, tabbladen — paste hij op meerdere elementen en werd het eerste gelezen.',
            'Bij varideck.nl paste de selector voor de maten op zeven paragrafen en werd de duurzaamheidstekst gelezen. Bij een prijs zou dat een geldig bedrag van een ánder artikel opleveren, zonder dat er iets van te zien is.',
            'Een nieuw aangewezen element levert nu altijd een selector op die precies dat ene element aanwijst.',
            'Bronnen die al waren ingericht dragen de oude selector nog. Past die op meerdere elementen, dan staat dat nu als waarschuwing in het logboek en bij de test: wijs het element opnieuw aan en het is opgelost.',
        ],
        commit: 'a0724b7',
    },
    {
        versie: 'V1.73',
        datum: '2026-08-18',
        titel: 'Uitklapblokken open, en een foutmelding die zegt wat er aan de hand is',
        punten: [
            'Staat de maattabel in een uitklapblok ("Afmetingen"), dan wordt dat nu opengezet — zowel in de configurator als bij de meting. Dicht stond het niet op de schermafdruk en was er niets aan te wijzen.',
            'Een bevestiging die niet in beeld staat wordt nu alsnog gelezen uit de pagina zelf.',
            '"Lengte kon niet worden bevestigd" zei twee heel verschillende dingen. Nu staat er of het element ontbreekt (met de tip dat een vastgelegde klik het kan dichtklappen), of welke tekst er wél is gelezen.',
        ],
        commit: '98d6963',
    },
    {
        versie: 'V1.72',
        datum: '2026-08-18',
        titel: 'Lengtes die niet te bevestigen waren',
        punten: [
            '"6mtr1" en "6 m1" worden nu als zes meter gelezen. Stond er op één regel ook een werkende breedte ("299 mm werkend en 6mtr1"), dan werd alleen die breedte gezien en werd de bron afgekeurd.',
            'Een specificatietabel met de eenheid vóór het getal — "Lengte per paneel: (mm) 6000" — wordt nu gelezen.',
            "Zo'n regel is nu ook aan te wijzen: staat het label in een eigen element en de waarde ernaast, dan werd alleen het label aangeboden, zonder de maat.",
            '"6 m²" wordt niet meer als 6 meter gelezen; een oppervlak is geen lengte.',
        ],
        commit: '4acf45e',
    },
    {
        versie: 'V1.71',
        datum: '2026-08-18',
        titel: 'Na het wisselen van bedrijf ververst het scherm ook echt',
        punten: [
            'Wisselde u van bedrijf terwijl u op het prijsoverzicht stond, dan wijzigde alleen het logo bovenaan en bleef de tabel van het vorige bedrijf staan.',
            'Nu wordt na een wissel het hele scherm opnieuw opgebouwd, met de gegevens van het nieuwe bedrijf.',
            'Dat geldt voor elk scherm, en ook filters en selecties beginnen weer schoon.',
        ],
        commit: 'd217144',
    },
    {
        versie: 'V1.70',
        datum: '2026-08-18',
        titel: 'Profielmenu met bedrijf wisselen en uitloggen',
        punten: [
            'Een klik op de initialen rechtsboven logde u meteen uit. Nu opent er een menu.',
            'In dat menu staat wie u bent, bij welk bedrijf u zit en met welke rol.',
            'Hebt u rechten voor meer bedrijven, dan wisselt u daar direct van bedrijf — uitloggen en opnieuw inloggen is niet meer nodig.',
            '"Uitloggen" staat onderaan het menu, als aparte knop.',
            'Het menu sluit met Escape of door ernaast te klikken.',
        ],
        commit: '8d5baee',
    },
    {
        versie: 'V1.69',
        datum: '2026-08-18',
        titel: 'De eigen productpagina komt uit de feed mee',
        punten: [
            'Levert de feed het adres van onze eigen productpagina — in een Google-feed heet dat veld "link" — dan wordt dat bij het aanmaken meteen vastgelegd.',
            'Daarmee staat de terugvalbron voor de eigen prijs er direct; die hoeft niet meer met de hand te worden opgezocht.',
            'In de keuzelijst uit de feed staat "eigen pagina" bij de artikelen waarvoor er een adres is gevonden.',
            'Staat er iets onbruikbaars in dat veld — een pad zonder domein bijvoorbeeld — dan wordt het product gewoon aangemaakt, alleen zonder pagina.',
        ],
        commit: '7419ef3',
    },
    {
        versie: 'V1.68',
        datum: '2026-08-18',
        titel: 'Alleen nog main en staging',
        punten: [
            'De losse werkbranch is opgeruimd; de werkafspraken en de overdracht noemen nu alleen main en staging.',
        ],
        commit: 'b80f952',
    },
    {
        versie: 'V1.67',
        datum: '2026-08-18',
        titel: 'De AI-analyse van een pagina werkt weer',
        punten: [
            'De AI koos stelselmatig de doorgestreepte oude prijs. Die wordt nu herkend en afgekeurd, en ook in de handmatige configurator niet meer als prijs aangeboden.',
            'De AI verwees naar elementen die niet bestonden (#10793) doordat de pixelpositie op dezelfde regel stond. De verwijzingen heten nu E1, E2, … en die positie is eruit.',
            'Een kleur die de webshop anders opschrijft — "Classic nerf Antraciet" tegenover onze "Antraciet classic nerf" — is geen reden meer om de pagina ongeschikt te verklaren.',
            'De optie die bij ons product past wordt in de lijst aangewezen, zodat de AI hem niet zelf hoeft te herkennen.',
            'Drie pogingen in plaats van twee, en elke afkeuring vertelt precies wat er mis was.',
        ],
        commit: 'c425ca3',
    },
    {
        versie: 'V1.66',
        datum: '2026-08-18',
        titel: 'De eigen prijsbron viel stil uit na een lengtecorrectie',
        punten: [
            'Een wijziging van lengte, kleur of folie zette álle bronnen van het product op pauze — ook de eigen prijsbron uit de XML-feed.',
            'Die bron was daarna niet meer aan te zetten: activeren vroeg om een geslaagde test, en een feedbron valt niet te testen.',
            'Gevolg: geen eigen prijs meer, zonder foutmelding en zonder mislukte meting. Hetzelfde gebeurde bij een import met een gewijzigd kenmerk.',
            'De feedbron blijft nu buiten die hertest, is met de hand te activeren, en bronnen die al vastzaten worden bij de eerstvolgende controle weer aangezet.',
            'Een bron die u zelf hebt gepauzeerd blijft gepauzeerd.',
        ],
        commit: '1ff02ac',
    },
    {
        versie: 'V1.65',
        datum: '2026-08-18',
        titel: 'De uitvoering vertelt wat de XML-feed deed',
        punten: [
            'Bij de laatste uitvoering staat nu een regel over de feed: hoeveel eigen prijzen er zijn gelezen.',
            'Deed er geen enkele eigen prijsbron mee, dan staat dat er nadrukkelijk; eerder was dat aan niets te zien.',
            'Is de feed niet te lezen — bijvoorbeeld omdat er voor dit bedrijf nog geen feed is ingesteld — dan staat de reden erbij.',
            'In het logboek staat bij zo\'n uitvoering "XML-feed niet gelezen" of "geen eigen prijsbron" in de opmerkingen.',
        ],
        commit: 'af4f53b',
    },
    {
        versie: 'V1.64',
        datum: '2026-08-18',
        titel: 'Feedprijs met meer dan twee decimalen',
        punten: [
            'Een prijs als "106.735431 EUR" uit een XML-feed werd als € 106.735,00 gelezen — duizend keer te hoog. Nu wordt het € 106,74.',
            'Hetzelfde gold voor bedragen onder een euro met drie decimalen: "0.999" werd € 999,00 in plaats van € 1,00.',
            'Een feed in Google Shopping-vorm (g:id, g:price) wordt gelezen zoals hij bedoeld is; dat staat nu ook in de tests vast.',
        ],
        commit: 'f7fc420',
    },
    {
        versie: 'V1.63',
        datum: '2026-08-18',
        titel: 'Prijs met de centen in een eigen element',
        punten: [
            'Bij bronnen die de centen apart zetten (87 , 33) werd stil € 87,00 vastgelegd in plaats van € 87,33.',
            'In de bronconfigurator is het hele bedrag nu aan te wijzen; eerst kon alleen de 87, de komma of de 33 los.',
            'Onderzocht of de sprong naar Prisma 7 kon: technisch wel, maar hij lost de kwetsbaarheidsmelding niet op. Niet gedaan; de bevinding staat in de overdracht.',
        ],
        commit: '8a13dae',
    },
    {
        versie: 'V1.62',
        datum: '2026-08-18',
        titel: 'Een grote import loopt niet meer vast',
        punten: [
            'Het doorvoeren van een import van enkele honderden rijen mislukte volledig; er werd dan niets aangemaakt.',
            'Een bestand van 2.000 rijen liep vast na vijf seconden en gaat nu in een halve seconde.',
            'Ook het maximum van 5.000 rijen wordt nu gehaald.',
        ],
        commit: 'b0539c3',
    },
    {
        versie: 'V1.61',
        datum: '2026-08-17',
        titel: 'Het logboek van uitvoeringen is langer',
        punten: [
            'Taken & logboek toont standaard 30 eerdere uitvoeringen in plaats van 6.',
            'Met de knoppen 30, 90 en 365 rechtsboven kies je zelf hoeveel er staan.',
            'Erboven staat hoeveel er worden getoond van hoeveel er in totaal zijn.',
        ],
        commit: '8ce3a69',
    },
    {
        versie: 'V1.60',
        datum: '2026-08-17',
        titel: 'Dezelfde periodekeuze op beide schermen, met 1 jaar erbij',
        punten: [
            '"1 jaar" toegevoegd achter 90 dagen, in zowel de prijsgrafiek als de prijshistorie.',
            '"7 dagen" is weg uit de prijsgrafiek; die keuze stond niet in de prijshistorie.',
            'De periodeknoppen komen nu uit één lijst, dus de twee schermen kunnen niet meer verschillen.',
        ],
        commit: 'ed9c363',
    },
    {
        versie: 'V1.59',
        datum: '2026-08-17',
        titel: 'Webshops op prijs gesorteerd, en het verschil met onze prijs in kleur',
        punten: [
            'De webshops staan van goedkoop naar duur, met onze eigen prijs altijd vooraan als ijkpunt.',
            'Te schakelen tussen "Op prijs" en "Op naam" in het prijsoverzicht en de prijshistorie.',
            'Op het productdetail schakelt een knop tussen de prijsvolgorde en de volgorde uit de configuratie.',
            'Het percentage t.o.v. onze prijs staat in een gekleurd vlakje: rood als de concurrent goedkoper is, groen als hij duurder is.',
            'De kleur wordt sterker naarmate het verschil groter is; onder 1% blijft hij grijs.',
            'Het verschil met onze prijs staat nu ook op een dag waarop de prijs is gewijzigd.',
        ],
        commit: '686f90f',
    },
    {
        versie: 'V1.58',
        datum: '2026-08-17',
        titel: 'Bronnaam naast de lijn, en lijnen die echt van elkaar verschillen',
        punten: [
            'Aan het eind van elke lijn staat nu de naam van de bron, in de kleur van die lijn.',
            'Vallen twee namen op dezelfde hoogte, dan worden ze uit elkaar geschoven met een streepje naar hun lijn.',
            'Tien duidelijk verschillende lijnkleuren in plaats van vijf tinten blauw en geel.',
            'Bij meer dan tien bronnen wordt de lijn gestreept, zodat hij niet met de eerste tien te verwarren is.',
            'Een bron die halverwege stopte met meten heeft zijn naam bij dat punt staan, niet aan de rechterrand.',
        ],
        commit: '3993048',
    },
    {
        versie: 'V1.57',
        datum: '2026-08-17',
        titel: 'Nieuw scherm Prijsgrafiek',
        punten: [
            'Nieuw menu-item Prijsgrafiek: het prijsverloop per bron als lijndiagram, met periodekeuze.',
            'Vier kengetallen bovenaan: eigen prijs, laagste marktprijs, dagen met wijziging, grootste beweging.',
            'Schakelaar "% t.o.v. eigen prijs": elke bron als afwijking van onze prijs, met nul als scheidslijn.',
            'Onderaan een klein diagram per product, zodat je ziet bij welk product je moet gaan kijken.',
            'Knoppen "Tabelweergave" en "Grafiekweergave" springen heen en weer met hetzelfde product en dezelfde periode.',
        ],
        commit: '9f28c0d',
    },
    {
        versie: 'V1.56',
        datum: '2026-08-17',
        titel: 'Alle e-mails in dezelfde opmaak',
        punten: [
            'Het prijswijzigingenoverzicht kwam in Outlook in een ander lettertype aan dan het foutoverzicht; dat is gelijkgetrokken.',
            'Ook het handmatige testbericht heeft nu dezelfde kaart, kop en voettekst.',
            'De drie berichten worden uit dezelfde bouwstenen opgebouwd, zodat ze niet opnieuw uit elkaar kunnen lopen.',
        ],
        commit: '53508ee',
    },
    {
        versie: 'V1.55',
        datum: '2026-08-17',
        titel: 'Het versiebeheer beschreven, en de werkafspraken bij de hand',
        punten: [
            'Nieuw document docs/versiebeheer.md: hoe dit versielogboek werkt en hoe je het elders inbouwt.',
            'CLAUDE.md in de projectwortel, zodat de afspraak "elke commit een versieregel" altijd meekomt.',
            'Drie nieuwe kwetsbaarheidsmeldingen in onderliggende pakketten verholpen (fast-uri, nanoid).',
        ],
        commit: '622b1c9',
    },
    {
        versie: 'V1.54',
        datum: '2026-07-29',
        titel: 'Versieoverzicht met een nummer per wijziging',
        punten: [
            'Linksonder in het menu staat het versienummer van de code die draait.',
            'Klikken opent een overzicht van alle versies met datum en wat er is veranderd.',
            'Alle 53 eerdere commits hebben met terugwerkende kracht een nummer gekregen.',
        ],
        commit: 'df8f7b7',
    },
    {
        versie: 'V1.53',
        datum: '2026-07-29',
        titel: 'Domein weer weg onder de bronnaam',
        punten: [
            'In kolomkoppen stond het domein in kapitalen onder een bijna gelijke naam; dat is eruit.',
            'Het link-pictogram naar de concurrentpagina en het artikelnummer blijven staan.',
        ],
        commit: '1c9cb7d',
    },
    {
        versie: 'V1.52',
        datum: '2026-07-29',
        titel: 'Artikelnummer onder de bronnaam en de laatste controle in de topbalk',
        punten: [
            'Het artikelnummer van de concurrent staat onder de bronnaam; de aparte kolom is weg.',
            'Rechtsboven staat op elk scherm "Laatste controle: vandaag 00:15 – 00:23".',
            'Loopt de controle nog, dan staat er "bezig…" in plaats van een eindtijd.',
        ],
        commit: '56c0c73',
    },
    {
        versie: 'V1.51',
        datum: '2026-07-29',
        titel: 'Bronnaam met logo, en de prijskolommen zoals in het ontwerp',
        punten: [
            'Bij de eigen webshop staat het bedrijfslogo in plaats van een sterretje.',
            'In de kolom Δ prijs staat "ongewijzigd" in plaats van een streepje bij een gelijke prijs.',
            'Bij de eigen regel staat "referentie" in de vergelijkingskolom.',
            'De dagprijs is dikgedrukt.',
        ],
        commit: '5fd2cb0',
    },
    {
        versie: 'V1.50',
        datum: '2026-07-29',
        titel: 'Bewijs zichtbaar bij een mislukte taak',
        punten: [
            'Bij elke mislukte bron in Taken & logboek staan de schermafdruk en de paginabron van die dag.',
            'Een HTML-fragment wordt als bestand aangeboden en niet meer als pagina gerenderd.',
            'Hoogstens 50 afdrukken per ronde bij prijswijzigingen van meer dan 5%.',
        ],
        commit: '908d98d',
    },
    {
        versie: 'V1.49',
        datum: '2026-07-29',
        titel: 'Gemiste nacht inhalen en oud materiaal opruimen',
        punten: [
            'Heeft de ronde van vannacht niet gelopen, dan wordt hij bij het opstarten ingehaald.',
            'Het prijsoverzicht waarschuwt wanneer de laatste controle ouder is dan 36 uur.',
            'Oude schermafdrukken, crawlpogingen en importregels worden opgeruimd; prijzen blijven altijd.',
        ],
        commit: '7adb8e5',
    },
    {
        versie: 'V1.48',
        datum: '2026-07-29',
        titel: 'Actieve producten in de configurator en een link naar de bron',
        punten: [
            'De productkeuze in de bronconfigurator toont alleen actief gemonitorde producten.',
            'Naast elke bronnaam staat een link naar de productpagina bij die concurrent.',
            'Een wisselvallige CI-test wacht nu op een tijdgrens in plaats van op een aantal pogingen.',
        ],
        commit: 'aea1565',
    },
    {
        versie: 'V1.47',
        datum: '2026-07-29',
        titel: 'De testronde van twaalf naar tweeënhalve minuut',
        punten: [
            'Twee wachttijden die altijd volliepen zijn weggehaald; dat scheelt ook tijd bij de meting.',
            'npm run verify:snel draait alles behalve de browsertests, in een halve minuut.',
        ],
        commit: '6d758da',
    },
    {
        versie: 'V1.46',
        datum: '2026-07-29',
        titel: 'Verborgen keuzelijsten en een voorspelbare klik op de afdruk',
        punten: [
            'Een keuzelijst op display:none wordt nu gebruikt; bij buitengevel.nl was de lengte daardoor niet te kiezen.',
            'De applicatie stelt zelf voor om de variant uit het adres te lezen (?attribute_pa_lengte=…).',
            'Klikvlakken op de afdruk liggen op oppervlak, zodat het kleinste element bovenop ligt.',
        ],
        commit: 'e6b7e15',
    },
    {
        versie: 'V1.45',
        datum: '2026-07-29',
        titel: 'Eén regel voor onze eigen prijs, ook op het productdetail',
        punten: [
            'Het productdetail en de dagmail toonden twee regels "Eigen webshop"; nu één.',
            'Het lijndiagram laat geen gat meer op een dag waarop de terugvalbron de prijs leverde.',
        ],
        commit: '90dc288',
    },
    {
        versie: 'V1.44',
        datum: '2026-07-29',
        titel: 'Voorbeeldweergave in verhouding, en analyses in een wachtrij',
        punten: [
            'De afdruk in de configurator wordt weer op een normaal venster gemaakt.',
            'Maatknoppen met role="radio" of data-value staan nu in de inventaris.',
            'De AI weigert een eenheidsprijs (per m¹) en zoekt de prijs per stuk.',
            'Knop "Alle bronnen": analyses lopen één voor één door op de achtergrond.',
            'De user-agent van de crawler bevat geen adres meer.',
        ],
        commit: 'd0ac6d0',
    },
    {
        versie: 'V1.43',
        datum: '2026-07-29',
        titel: 'Logo per bedrijf in plaats van de naam rechtsboven',
        punten: [
            'Een logo per bedrijf in te stellen via Instellingen → Logo (PNG, JPEG of WebP).',
            'Het logo staat rechtsboven en in het bedrijfskeuzescherm.',
        ],
        commit: '3ef99e8',
    },
    {
        versie: 'V1.42',
        datum: '2026-07-28',
        titel: 'Pagina analyseren met AI',
        punten: [
            'Knop "Pagina analyseren": de AI stelt een crawlmethode voor en test die meteen.',
            'Elementen onder de afkapgrens worden met naam en aantal gemeld.',
        ],
        commit: '5c81f92',
    },
    {
        versie: 'V1.41',
        datum: '2026-07-28',
        titel: 'Plakkende balken als redmiddel, en de paginabron op te vragen',
        punten: [
            'Plakkende balken worden alleen losgemaakt ná een mislukte klik.',
            'Knop "Paginabron" downloadt de opgebouwde HTML zoals de crawler die ziet.',
        ],
        commit: '236612b',
    },
    {
        versie: 'V1.40',
        datum: '2026-07-28',
        titel: 'De laagste prijs valt op in het prijsoverzicht',
        punten: ['Dezelfde gele markering voor de laagste prijs als in de prijshistorie.'],
        commit: '43712fa',
    },
    {
        versie: 'V1.39',
        datum: '2026-07-28',
        titel: 'Pagina op ware hoogte inspecteren, en klikken die aankomen',
        punten: [
            'De afkapgrens van de afdruk ging van 6.000 naar 14.000 px.',
            'Een klikladder voor elementen die de klik niet zelf aannemen.',
        ],
        commit: '752f80b',
    },
    {
        versie: 'V1.38',
        datum: '2026-07-28',
        titel: 'Eigen webshop als terugvalbron, en AI-verbruik met kosten',
        punten: [
            'De eigen webshoppagina als terugval wanneer de feed geen prijs geeft.',
            'Nieuw scherm AI-verbruik met tokens en kosten per dag, maand en jaar.',
            'HANDOFF.md voor een volgende ontwikkelsessie.',
        ],
        commit: '2e06035',
    },
    {
        versie: 'V1.37',
        datum: '2026-07-28',
        titel: 'README en architectuur bijgewerkt',
        punten: ['Documentatie in lijn gebracht met de gebouwde stand.'],
        commit: '3d360a7',
    },
    {
        versie: 'V1.36',
        datum: '2026-07-28',
        titel: 'Getalkolommen lijnen weer uit met hun kop',
        punten: ['Een CSS-regel liet de bedragen uit de kolomkoppen lopen.'],
        commit: 'b28c833',
    },
    {
        versie: 'V1.35',
        datum: '2026-07-28',
        titel: 'Cookies accepteren, en bronnen overnemen per product',
        punten: [
            'Cookiemeldingen worden geaccepteerd; weigeren blokkeerde de prijs op sommige webshops.',
            'De applicatie wacht tot de pagina niet meer verandert in plaats van op elke DOM-wijziging.',
            '"Neem prijsbronnen over" kopieert de bronnen van een ander product.',
        ],
        commit: '5bf0c38',
    },
    {
        versie: 'V1.34',
        datum: '2026-07-28',
        titel: 'Btw per bron, bronnamen en bedrijvenbeheer',
        punten: [
            'De btw-aanname is per bron instelbaar.',
            'Bronnamen komen uit het domein in plaats van het eerste label van de hostnaam.',
            'Instellingen worden hergebruikt bij dezelfde webshop.',
            'Bedrijven aanmaken en beheren, met gebruikers per bedrijf.',
        ],
        commit: '0104679',
    },
    {
        versie: 'V1.33',
        datum: '2026-07-28',
        titel: 'Kandidaten zoeken op de achtergrond, en de aanbiedingsprijs',
        punten: [
            'Het zoeken naar kandidaat-URL’s loopt op de achtergrond met voortgang in het scherm.',
            'De eigen prijs gebruikt de aanbiedingsprijs uit de feed wanneer die er is.',
        ],
        commit: '0c1128d',
    },
    {
        versie: 'V1.32',
        datum: '2026-07-28',
        titel: 'AI_TIMEOUT_MS eenduidig gedocumenteerd',
        punten: ['De betekenis van de wachttijd stond op twee plaatsen verschillend beschreven.'],
        commit: '14df074',
    },
    {
        versie: 'V1.31',
        datum: '2026-07-28',
        titel: 'Prijs met tekst eromheen, en bulkacties op producten',
        punten: [
            'Een bedrag met een eenheid in hetzelfde element wordt herkend als prijs.',
            'Producten in bulk activeren, pauzeren of archiveren.',
        ],
        commit: '934cd3e',
    },
    {
        versie: 'V1.30',
        datum: '2026-07-28',
        titel: 'Elke storing bij de AI-dienst geeft een leesbare melding',
        punten: ['Een ontbrekend model, een geweigerde sleutel of geen tegoed noemt nu de oorzaak.'],
        commit: 'fe70b8e',
    },
    {
        versie: 'V1.29',
        datum: '2026-07-28',
        titel: 'Eigen prijsbron, btw-tekst en de afdruk van de hele pagina',
        punten: [
            'Elk product krijgt een eigen prijsbron uit de feed.',
            'Een prijs zonder valutateken wordt herkend.',
            'De afdruk in de configurator beslaat de hele pagina in plaats van het venster.',
        ],
        commit: '11741c1',
    },
    {
        versie: 'V1.28',
        datum: '2026-07-28',
        titel: 'Controle starten met een knop, en filters op het overzicht',
        punten: [
            'De dagelijkse controle is met een knop te starten in plaats van te wachten op 00:15.',
            'De browser wordt ook gevonden bij een systeeminstallatie van Chromium.',
            'Filteren op gewijzigd of fouten in het prijsoverzicht.',
        ],
        commit: 'b622b74',
    },
    {
        versie: 'V1.27',
        datum: '2026-07-28',
        titel: 'Maatvoering, prijselementen en de hele pagina in de voorbeeldweergave',
        punten: [
            'Bij een maatvoering als "6000 x 250 x 10 mm" gelden alle getallen, niet alleen het eerste.',
            'Een prijs die over meerdere elementen is verdeeld is aan te wijzen.',
        ],
        commit: '7e93bb9',
    },
    {
        versie: 'V1.26',
        datum: '2026-07-28',
        titel: 'Zoekaanwijzing, feedvelden en kleuren beheren',
        punten: [
            'Een eigen aanwijzing mee te geven aan de AI-zoekopdracht.',
            'Een bron verwijderen, en kleuren per product beheren.',
        ],
        commit: 'c3348f5',
    },
    {
        versie: 'V1.25',
        datum: '2026-07-28',
        titel: 'Drie fouten uit de crawltest op echte webshops',
        punten: ['Variantcontrole, prijsherkenning en foutmeldingen aangescherpt op echte pagina’s.'],
        commit: 'ae31a13',
    },
    {
        versie: 'V1.24',
        datum: '2026-07-28',
        titel: 'Kandidaat-URL’s zoeken met OpenAI',
        punten: [
            'De AI zoekt concurrentpagina’s; de eigen crawler verifieert elk voorstel voordat het wordt aangeboden.',
        ],
        commit: 'f430c31',
    },
    {
        versie: 'V1.23',
        datum: '2026-07-28',
        titel: 'Pagina-inspectie gaf altijd een 400',
        punten: ['Het verzamelscript werd meegegeven maar nooit aangeroepen.'],
        commit: '7ec20ad',
    },
    {
        versie: 'V1.22',
        datum: '2026-07-28',
        titel: 'Producten kiezen uit de feed, en foutregistratie via Sentry',
        punten: [
            'Producten aanmaken door ze uit de XML-feed te kiezen.',
            'Onverwachte fouten gaan naar Sentry, zonder persoonsgegevens.',
        ],
        commit: '858fbc7',
    },
    {
        versie: 'V1.21',
        datum: '2026-07-28',
        titel: 'Testcommando voor de e-mailkoppeling',
        punten: ['npm run mail:test controleert Microsoft 365 zonder de dagelijkse ronde te draaien.'],
        commit: 'a7e3c31',
    },
    {
        versie: 'V1.20',
        datum: '2026-07-27',
        titel: 'Montserrat zelf hosten en CSP zonder externe hosts',
        punten: [
            'Het lettertype komt van onze eigen server; er staat geen externe host in het beleid.',
        ],
        commit: '11afe6d',
    },
    {
        versie: 'V1.19',
        datum: '2026-07-27',
        titel: 'Topbar-titel op het productdetail en uitlijning van de grafieklabels',
        punten: ['Twee opmaakfouten in het productdetail.'],
        commit: 'a241641',
    },
    {
        versie: 'V1.18',
        datum: '2026-07-27',
        titel: 'H10: instellingen — toegang, ontvangers en de XML-feed',
        punten: [
            'Toegang en rollen per bedrijf beheren.',
            'E-mailontvangers per soort overzicht.',
            'De feed-URL versleuteld opslaan en alleen gemaskeerd tonen.',
        ],
        commit: 'a7648a1',
    },
    {
        versie: 'V1.17',
        datum: '2026-07-27',
        titel: 'H9: prijsoverzicht, productdetail, historie en Excel-export',
        punten: [
            'De prijsmatrix met producten tegen webshops.',
            'Productdetail met dagrecords en een lijndiagram.',
            'Prijshistorie per periode, en export naar Excel.',
        ],
        commit: '54cc1ba',
    },
    {
        versie: 'V1.16',
        datum: '2026-07-27',
        titel: 'H8: dagelijkse controle, wijzigingsdetectie en e-mail',
        punten: [
            'De nachtelijke ronde om 00:15 Nederlandse tijd.',
            'Wijzigingen tegen de laatst geldige meting, niet tegen gisteren.',
            'Dagelijkse overzichten en foutmeldingen per e-mail.',
        ],
        commit: '520785c',
    },
    {
        versie: 'V1.15',
        datum: '2026-07-27',
        titel: 'H7: prijsbronnen en de visuele bronconfigurator',
        punten: [
            'Concurrent-URL’s toevoegen en per bron de crawlstappen vastleggen.',
            'Aanwijzen door op een schermafdruk van de echte pagina te klikken.',
        ],
        commit: '8bd5378',
    },
    {
        versie: 'V1.14',
        datum: '2026-07-27',
        titel: 'H6: XML-feed, crawl-engine, variantvalidatie en bewijs',
        punten: [
            'De eigen prijs uit de Sales Layer XML-feed.',
            'Prijzen lezen met een headless browser, met controle op de juiste variant.',
            'Schermafdrukken en paginafragmenten als bewijs bij fouten.',
        ],
        commit: '94b00ad',
    },
    {
        versie: 'V1.13',
        datum: '2026-07-27',
        titel: 'Deploy: het commando als symlink naar de productie-checkout',
        punten: ['Zodat een nieuwe versie van het script meteen actief is.'],
        commit: 'd7f0771',
    },
    {
        versie: 'V1.12',
        datum: '2026-07-27',
        titel: 'Deploy: devDependencies expliciet meenemen',
        punten: ['npm ci sloeg ze over, waardoor de build op de server niet lukte.'],
        commit: '192ecc9',
    },
    {
        versie: 'V1.11',
        datum: '2026-07-27',
        titel: 'CI herstellen',
        punten: ['Opmaak van een testbestand en een verify-opdracht die ontbrak.'],
        commit: '11d46c2',
    },
    {
        versie: 'V1.10',
        datum: '2026-07-27',
        titel: 'Deploy: GITHUB_TOKEN uit het instellingenbestand',
        punten: ['Zodat er geen token in de git-configuratie of in de logboeken belandt.'],
        commit: 'b410d2f',
    },
    {
        versie: 'V1.09',
        datum: '2026-07-27',
        titel: 'Deploy: GitHub-toegang voor de applicatiegebruiker',
        punten: ['Gedocumenteerd hoe de server bij de repository komt.'],
        commit: '0b41889',
    },
    {
        versie: 'V1.08',
        datum: '2026-07-27',
        titel: 'Seed: alleen e.heuven als standaardbeheerder',
        punten: ['Geen voorbeeldgebruikers in een verse database.'],
        commit: '84db959',
    },
    {
        versie: 'V1.07',
        datum: '2026-07-27',
        titel: 'Deploy: de bestaande /opt/swg-indeling van de server volgen',
        punten: ['De applicatie past zich aan de server aan, niet andersom.'],
        commit: 'edec2bb',
    },
    {
        versie: 'V1.06',
        datum: '2026-07-27',
        titel: 'Deploy: eigen commando per project en een runbook',
        punten: [
            'swg-prijsmonitor-deploy staging|production, zodat het bestaande swg-deploy blijft werken.',
        ],
        commit: '6ab02d5',
    },
    {
        versie: 'V1.05',
        datum: '2026-07-27',
        titel: 'H5: producten en Excel-import',
        punten: [
            'Producten beheren met lengte, kleur en folie, en welke daarvan kritisch zijn.',
            'Assortiment importeren uit Excel, met een controle vóór het opslaan.',
        ],
        commit: '5caf989',
    },
    {
        versie: 'V1.04',
        datum: '2026-07-27',
        titel: 'H4: deploy naar staging en productie',
        punten: ['Twee omgevingen achter nginx, met systemd en Node 22.'],
        commit: '07993e4',
    },
    {
        versie: 'V1.03',
        datum: '2026-07-27',
        titel: 'H3: app-shell en design system',
        punten: ['Navigatie, kleuren, typografie en de bouwstenen voor de schermen.'],
        commit: 'cc62cf6',
    },
    {
        versie: 'V1.02',
        datum: '2026-07-27',
        titel: 'H2: authenticatie, toegangslijst en bedrijfsscheiding',
        punten: [
            'Inloggen met Microsoft Entra, alleen voor adressen op de toegangslijst.',
            'Rollen per bedrijf, en gegevens die nooit tussen bedrijven kunnen weglekken.',
        ],
        commit: 'dd903c5',
    },
    {
        versie: 'V1.01',
        datum: '2026-07-27',
        titel: 'H1: fundament — monorepo, datamodel, config en CI',
        punten: [
            'De opzet van het project: workspaces, TypeScript, PostgreSQL met Prisma.',
            'Configuratie met controle bij het opstarten, en CI die build, lint en tests draait.',
        ],
        commit: 'fcb9fb5',
    },
];
//# sourceMappingURL=versies.js.map